<template>
  <div class="container mt-4">
    <!-- Header -->
    <div class="d-flex justify-content-between align-items-center mb-4">
      <h4 class="mb-0">
        <i class="material-icons me-2">certificate</i>
        {{ $t('certificateGenerate.title') || 'Générer un Certificat' }}
      </h4>
      <button class="btn btn-secondary" @click="goBack">
        <i class="material-icons me-1">arrow_back</i>
        {{ $t('certificateGenerate.back') || 'Retour' }}
      </button>
    </div>

    <!-- Form Card -->
    <div class="card shadow border-0">
      <div
        class="card-header text-white fw-bold"
        style="background: linear-gradient(195deg, #dc3545, #c82333);"
      >
        <i class="material-icons me-2">settings</i>
        {{ $t('certificateGenerate.formTitle') || 'Paramètres du Certificat' }}
      </div>

      <div class="card-body">
        <form @submit.prevent="generateCertificate">
          <!-- Language Selection -->
          <div class="row mb-4">
            <div class="col-md-6">
              <label for="language" class="form-label fw-bold">
                <i class="material-icons me-1">language</i>
                {{ $t('certificateGenerate.language') || 'Langue' }}
                <span class="text-danger">*</span>
              </label>
              <select
                v-model="form.language"
                class="form-select"
                id="language"
                required
              >
                <option value="" disabled>
                  {{ $t('certificateGenerate.selectLanguage') || 'Sélectionner une langue' }}
                </option>
                <option value="ar">{{ $t('certificateGenerate.languages.arabic') || 'Arabe' }}</option>
                <option value="fr">{{ $t('certificateGenerate.languages.french') || 'Français' }}</option>
                <option value="en">{{ $t('certificateGenerate.languages.english') || 'Anglais' }}</option>
              </select>
            </div>
          </div>

          <!-- Employee Selection -->
          <div class="row mb-4">
            <div class="col-md-6">
              <label for="employee" class="form-label fw-bold">
                <i class="material-icons me-1">person</i>
                {{ $t('certificateGenerate.employee') || 'Employé' }}
                <span class="text-danger">*</span>
              </label>
              <select
                v-model="form.employeeId"
                class="form-select"
                id="employee"
                required
                :disabled="loadingEmployees"
              >
                <option value="" disabled>
                  {{ loadingEmployees ? ($t('certificateGenerate.loadingEmployees') || 'Chargement des employés...') : ($t('certificateGenerate.selectEmployee') || 'Sélectionner un employé') }}
                </option>
                <option
                  v-for="employee in employees"
                  :key="employee.id"
                  :value="employee.id"
                >
                  {{ employee.firstname }} {{ employee.lastname }} - {{ employee.email }}
                </option>
              </select>
            </div>
          </div>

          <!-- Certificate Type Selection -->
          <div class="row mb-4">
            <div class="col-md-6">
              <label for="certificateType" class="form-label fw-bold">
                <i class="material-icons me-1">description</i>
                {{ $t('certificateGenerate.certificateType') || 'Type de certificat' }}
                <span class="text-danger">*</span>
              </label>
              <select
                v-model="form.certificateTypeId"
                class="form-select"
                id="certificateType"
                required
                :disabled="loadingTypes"
              >
                <option value="" disabled>
                  {{ loadingTypes ? ($t('certificateGenerate.loadingTypes') || 'Chargement des types...') : ($t('certificateGenerate.selectType') || 'Sélectionner un type') }}
                </option>
                <option
                  v-for="type in certificateTypes"
                  :key="type.id"
                  :value="type.id"
                >
                  {{ type.name }}
                </option>
              </select>
            </div>
          </div>

          <!-- Action Buttons -->
          <div class="d-flex gap-3 justify-content-end">
            <button
              type="button"
              class="btn btn-secondary"
              @click="resetForm"
              :disabled="generating"
            >
              <i class="material-icons me-1">refresh</i>
              {{ $t('certificateGenerate.reset') || 'Réinitialiser' }}
            </button>
            <button
              type="submit"
              class="btn btn-dark text-white"
              :disabled="generating || !isFormValid"
            >
              <i class="material-icons me-1" v-if="!generating">picture_as_pdf</i>
              <span class="spinner-border spinner-border-sm me-1" v-else></span>
              {{ generating ? ($t('certificateGenerate.generating') || 'Génération...') : ($t('certificateGenerate.generatePdf') || 'Générer PDF') }}
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- Error Alert -->
    <div v-if="errorMessage" class="alert alert-danger mt-3" role="alert">
      <i class="material-icons me-2">error</i>
      {{ errorMessage }}
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue'
import { useRouter } from 'vue-router'
import axios from '@/api'
import { useToast } from 'vue-toastification'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()
const toast = useToast()
const router = useRouter()

const form = ref({
  language: '',
  employeeId: '',
  certificateTypeId: ''
})

const employees = ref([])
const certificateTypes = ref([])
const loadingEmployees = ref(false)
const loadingTypes = ref(false)
const generating = ref(false)
const errorMessage = ref('')

const isFormValid = computed(() => {
  return form.value.language && form.value.employeeId && form.value.certificateTypeId
})

const goBack = () => {
  router.push('/employee/certificate-requests/review')
}

const resetForm = () => {
  form.value = {
    language: '',
    employeeId: '',
    certificateTypeId: ''
  }
  errorMessage.value = ''
}

const fetchEmployees = async () => {
  loadingEmployees.value = true
  try {
    console.log('Fetching ALL employees from /getAllUser with pagination...')
    employees.value = []

    let page = 1
    let lastPage = 1
    const batchSize = 100

    do {
      console.log(`Fetching page ${page}...`)
      const res = await axios.get('/getAllUser', {
        params: { page, per_page: batchSize }
      })

      const data = Array.isArray(res.data?.data) ? res.data.data : []
      employees.value.push(...data)
      console.log(`Page ${page} loaded: ${data.length} employees`)

      lastPage = res.data?.last_page || 1
      page++
    } while (page <= lastPage)

    console.log(`Total employees loaded: ${employees.value.length}`)
  } catch (error) {
    console.error('Error fetching employees:', error)
    let errorMessage = 'Erreur lors du chargement des employés'
    
    if (error.response?.status === 403) {
      errorMessage = "Accès non autorisé. Veuillez vérifier vos permissions."
    } else if (error.response?.status === 401) {
      errorMessage = "Session expirée. Veuillez vous reconnecter."
      setTimeout(() => router.push('/sign-in'), 2000)
    } else if (error.response?.data?.message) {
      errorMessage = error.response.data.message
    }
    
    toast.error(errorMessage)
  } finally {
    loadingEmployees.value = false
  }
}

const fetchCertificateTypes = async () => {
  loadingTypes.value = true
  try {
    console.log('Fetching ALL certificate types with pagination...')
    certificateTypes.value = []

    let page = 1
    let lastPage = 1
    const batchSize = 100

    do {
      console.log(`Fetching certificate types page ${page}...`)
      const res = await axios.get('/certificate-types', {
        params: { page, per_page: batchSize }
      })

      const data = Array.isArray(res.data?.data?.data) ? res.data.data.data : 
                   Array.isArray(res.data?.data) ? res.data.data : []
      certificateTypes.value.push(...data)
      console.log(`Certificate types page ${page} loaded: ${data.length} types`)

      lastPage = res.data?.data?.last_page || res.data?.last_page || 1
      page++
    } while (page <= lastPage)

    console.log(`Total certificate types loaded: ${certificateTypes.value.length}`)
  } catch (error) {
    console.error('Error fetching certificate types:', error)
    let errorMessage = 'Erreur lors du chargement des types de certificats'
    
    if (error.response?.status === 403) {
      errorMessage = "Accès non autorisé. Veuillez vérifier vos permissions."
    } else if (error.response?.status === 401) {
      errorMessage = "Session expirée. Veuillez vous reconnecter."
      setTimeout(() => router.push('/sign-in'), 2000)
    } else if (error.response?.data?.message) {
      errorMessage = error.response.data.message
    }
    
    toast.error(errorMessage)
  } finally {
    loadingTypes.value = false
  }
}

const generateCertificate = async () => {
  if (!isFormValid.value) return

  generating.value = true
  errorMessage.value = ''

  const loadingToast = toast.info(
    t('certificateGenerate.messages.generatingPdf'),
    { timeout: false }
  )

  try {
    // Step 1: Create certificate request first
    const requestData = {
      user_id: form.value.employeeId,
      certificate_type_id: form.value.certificateTypeId,
      language: form.value.language,
      remarks: 'Generated directly by admin'
    }

    const createResponse = await axios.post('/certificate-requests', requestData)
    const requestId = createResponse.data.data?.id || createResponse.data.id

    if (!requestId) {
      throw new Error('Failed to create certificate request')
    }

    // Step 2: Mark as ready and generate PDF using existing API
    await axios.patch(`/certificate-requests/${requestId}/ready`)

    // Step 3: Generate PDF using the same API as CertificateReview
    const response = await axios.get(
      `/certificate-requests/${requestId}/generate`, 
      {
        responseType: 'blob' 
      }
    )

    // Create blob and download
    const blob = new Blob([response.data], {
      type: 'application/pdf'
    })

    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    
    // Generate filename
    const employee = employees.value.find(emp => emp.id === form.value.employeeId)
    const type = certificateTypes.value.find(t => t.id === form.value.certificateTypeId)
    const filename = `certificate_${employee?.firstname}_${employee?.lastname}_${type?.name}_${form.value.language}.pdf`
    
    link.download = filename
    link.click()
    window.URL.revokeObjectURL(url)

    toast.dismiss(loadingToast)
    toast.success(t('certificateGenerate.messages.success'))
    
    // Reset form after successful generation
    resetForm()
  } catch (error) {
    console.error('Error generating certificate:', error)
    toast.dismiss(loadingToast)
    
    if (error.response?.data instanceof Blob) {
      // Try to read error message from blob
      try {
        const errorText = await error.response.data.text()
        const errorData = JSON.parse(errorText)
        errorMessage.value = errorData.message || t('certificateGenerate.messages.error')
      } catch {
        errorMessage.value = t('certificateGenerate.messages.error')
      }
    } else {
      errorMessage.value = error.response?.data?.message || t('certificateGenerate.messages.error')
    }
    
    toast.error(errorMessage.value)
  } finally {
    generating.value = false
  }
}

onMounted(() => {
  // Vérifier si l'utilisateur est authentifié
  const token = localStorage.getItem('token')
  if (!token) {
    router.push('/sign-in')
    return
  }
  
  fetchEmployees()
  fetchCertificateTypes()
})
</script>

<style scoped>
.card {
  border-radius: 15px;
}

.card-header {
  border-radius: 15px 15px 0 0;
}

.form-label {
  color: #495057;
}

.btn {
  border-radius: 8px;
  padding: 0.5rem 1.5rem;
  font-weight: 500;
}

.btn-dark {
  background: linear-gradient(195deg, #6c757d, #5a6268);
  border: none;
}

.btn-dark:hover:not(:disabled) {
  background: linear-gradient(195deg, #5a6268, #495057);
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(108, 117, 125, 0.3);
}

.btn-secondary {
  background: #6c757d;
  border: none;
}

.btn-secondary:hover:not(:disabled) {
  background: #5a6268;
  transform: translateY(-1px);
}

.form-select:focus {
  border-color: #dc3545;
  box-shadow: 0 0 0 0.2rem rgba(220, 53, 69, 0.25);
}

.spinner-border-sm {
  width: 1rem;
  height: 1rem;
}

.alert {
  border-radius: 10px;
  border: none;
}

.material-icons {
  font-size: 1.2rem;
}
</style>
